import React, { useState, useEffect, useMemo } from 'react';
import { ToastContainer, toast } from "react-toastify";
import StudentTemplate from "../../component/StudentTemplate";
import Api from '../../component/Api';
import Select from "react-select";
import CreatableSelect from 'react-select/creatable';


const AcademicCredit = () => {
  // --- STATE MANAGEMENT ---
  const [isLoading, setIsLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [disciplines, setDisciplines] = useState([]);
  const [nheqfLevels, setNheqfLevels] = useState([]);
  const [courseCategory, setCourseCategory] = useState([]);
  const [applications, setApplications] = useState([]);
  const [approvedCourses, setApprovedCourses] = useState([]);
  const [showOtherPlatform, setShowOtherPlatform] = useState(false);

  const [courseInputValue, setCourseInputValue] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);




  // Form State
  const [formData, setFormData] = useState({
    courseName: '',
    discipline: '',
    category: '',
    nheqfLevel: '',
    platform: '',
    credits: '',
    gradeScale: '',
    mode: '',
    grade: '',
    gradePoint: '',
    percentage: '',
    abcAvailable: false,
    document: null,
  });


  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handlePreview = (doc) => {
    if (isMobile) {
      window.open(doc.url, '_blank');
    } else {
      setPreviewDoc(doc);
    }
  };

  const { http } = Api();

  const creditOptions = ['7.0', '6.5', '6.0', '5.5', '5.0', '4.5', '4.0', '3.5', '3.0', '2.5', '2.0', '1.5', '1.0'];
  const grades = ['A+', 'A', 'B+', 'B', 'C+', 'C', 'D'];
  const gradeOptions = grades.map((g) => ({
    value: g,
    label: g
  }));
  const creditSelectOptions = creditOptions.map((c) => ({
    value: c,
    label: c
  }));

  const gradeScaleOptions = [
    { value: "10", label: "10 Point Scale" },
    { value: "7", label: "7 Point Scale" },
    { value: "5", label: "5 Point Scale" },
    { value: "4", label: "4 Point Scale" },
  ];

  const selectStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: "#fff",
      border: "1px solid #dee2e6",
      boxShadow: "none",
      minHeight: "48px",
      borderRadius: "0.375rem",
    }),

    valueContainer: (base) => ({
      ...base,
      padding: "0 12px",
    }),

    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
    }),

    placeholder: (base) => ({
      ...base,
      color: "#6c757d",
    }),

    singleValue: (base) => ({
      ...base,
      color: "#212529",
    }),

    indicatorsContainer: (base) => ({
      ...base,
      height: "48px",
    }),

    menu: (base) => ({
      ...base,
      zIndex: 9999,
    }),

    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  const fetchApplications = async () => {
    try {
      const res = await http.get('/Student/getActiveApplications');
      if (res.data.status) {
        setApplications(res.data.data);
      }
    } catch (err) {
      console.error('Failed to load application history', err);
    }
  };

  useEffect(() => {
    const fetchDisciplines = async () => {
      try {
        const res = await http.get('/Student/getAllDisciplines');
        if (res.data.status) {
          setDisciplines(res.data.data);
          setNheqfLevels(res.data.levels);
          setCourseCategory(res.data.category);
          setApprovedCourses(res.data.approvedCourses);
        }
      } catch (err) {
        console.error('Failed to load disciplines', err);
      }
    };

    fetchDisciplines();
    fetchApplications();
  }, []);

  const disciplineOptions = useMemo(
    () =>
      disciplines.map((d) => ({
        value: d.id,
        label: d.discipline_name,
      })),
    [disciplines]
  );

  const courseOptions = useMemo(() => {
    if (!approvedCourses || approvedCourses.length === 0) return [];

    const uniqueMap = new Map();
    approvedCourses.forEach(app => {
      if (app.course_name && !uniqueMap.has(app.course_name)) {
        uniqueMap.set(app.course_name, {
          value: app.course_name,
          label: app.course_name,
          data: app
        });
      }
    });
    return Array.from(uniqueMap.values());
  }, [approvedCourses]);



  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCourseNameChange = (newValue) => {
    if (!newValue) {
      setFormData(prev => ({
        ...prev,
        courseName: '',
        course_id: '',
      }));
      return;
    }

    const isNew = newValue.__isNew__;
    const appData = newValue.data || {};

    if (isNew) {
      setFormData(prev => ({
        ...prev,
        course_id: null,
        courseName: newValue.label,

        discipline: '',
        category: '',
        nheqfLevel: '',
        platform: '',
        credits: '',
        mode: '',
        gradeScale: '',

        grade: '',
        percentage: '',
        gradePoint: '',
        abcAvailable: false,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        course_id: newValue.value,
        courseName: newValue.label,

        discipline: appData.discipline_id || '',
        category: appData.course_category || '',
        nheqfLevel: appData.course_level || '',
        platform: appData.platform || '',
        credits: appData.acquired_credits
          ? String(appData.acquired_credits)
          : '',
        mode: appData.mode || '',
        gradeScale: appData.grade_scale || '',

        grade: '',
        percentage: '',
        gradePoint: '',
        abcAvailable: false,
      }));
    }
  };
  const resetForm = () => {
    setFormData({
      courseName: '',
      discipline: '',
      category: '',
      nheqfLevel: '',
      platform: '',
      credits: '',
      gradeScale: '',
      mode: '',
      grade: '',
      gradePoint: '',
      percentage: '',
      abcAvailable: false,
      document: null,
    });
    setDocuments([]);
    setCourseInputValue(''); // resets the CreatableSelect input text
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);

    const mappedFiles = files.map((file) => ({
      file,
      name: file.name,
      size: `${(file.size / 1024).toFixed(1)} KB`,
      url: URL.createObjectURL(file),
    }));

    setDocuments((prev) => [...prev, ...mappedFiles]);
    e.target.value = null;
  };

  const removeFile = (index) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();


    if (!formData.courseName.trim()) {
      toast.error("Course Name is required.");
      return;
    }

    if (!formData.category) {
      toast.error("Course Category is required.");
      return;
    }

    if (!formData.nheqfLevel) {
      toast.error("NHEQF Level is required.");
      return;
    }

    if (!formData.discipline) {
      toast.error("Discipline is required.");
      return;
    }

    if (!formData.platform.trim()) {
      toast.error("Platform is required.");
      return;
    }

    if (!formData.mode) {
      toast.error("Mode (Online/Offline) is required.");
      return;
    }

    if (!formData.credits) {
      toast.error("Credit Weightage is required.");
      return;
    }

    if (!formData.gradeScale) {
      toast.error("Grade Scale is required.");
      return;
    }

    if (!formData.abcAvailable) {
      toast.error("Credits must be available in ABC to submit.");
      return;
    }
    if (!formData.grade) {
      toast.error("Grade is required.");
      return;
    }

    if (!formData.gradePoint.trim()) {
      toast.error("Grade Point is required.");
      return;
    }
    if (!formData.percentage) {
      toast.error("Percentage is required.");
      return;
    }
    if (Number(formData.percentage) < 0 || Number(formData.percentage) > 100) {
      toast.error("Percentage must be between 0 and 100.");
      return;
    }
    if (documents.length === 0) {
      toast.error("Please upload at least one certificate document.");
      return;
    }

    // setIsLoading(true);
    // if (!formData.abcAvailable) {
    //   toast.error("ABC Credits must be available to submit.");
    //   return;
    // }

    setIsLoading(true);

    try {
      const payload = new FormData();

      payload.append('course_name', formData.courseName);
      payload.append('discipline_id', formData.discipline);
      payload.append('course_category', formData.category);
      payload.append('course_level', formData.nheqfLevel);
      payload.append('platform', formData.platform);
      payload.append('mode', formData.mode);
      payload.append('acquired_credits', formData.credits);
      payload.append('grade_scale', formData.gradeScale);
      payload.append('grade', formData.grade);
      payload.append('grade_point', formData.gradePoint);
      payload.append('percentage', formData.percentage);
      payload.append('ABC_status', formData.abcAvailable ? 1 : 0);

      documents.forEach((doc) => {
        payload.append('documents[]', doc.file);
      });

      const res = await http.post('/Student/submitExternalCredit', payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.status) {
        toast.success(res.data.message || 'Course details submitted successfully!');
        fetchApplications();
        resetForm();
      } else {
        toast.error(res.data.message || 'Submission failed.');
      }

    } catch (error) {
      console.error(error);
      if (error.response && error.response.data && error.response.data.errors) {
        toast.error("Please check all required fields.");
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (label) => {
    const s = (label || '').toLowerCase();

    const statusColors = {
      'submitted by student': 'bg-warning text-dark border-warning',
      'forwarded to principal': 'bg-info text-dark border-info',
      'forwarded to university': 'bg-primary text-white border-primary',
      // 'forwarded to bos': 'bg-secondary text-white border-secondary',

      // 'bos approved': 'bg-success text-white border-success',
      'application approved': 'bg-success text-white border-success',

      'rejected by sfa': 'bg-danger text-white border-danger',
      'rejected by principal': 'bg-danger text-white border-danger',
      'rejected by university': 'bg-danger text-white border-danger',
      'course rejected': 'bg-danger text-white border-danger'
    };

    return statusColors[s] || 'bg-light text-dark border-secondary';
  };

  const platformOptions = [
    { value: "SWAYAM", label: "SWAYAM" },
    { value: "NPTEL", label: "NPTEL" },
    // { value: "other", label: "Others" }
  ];

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const stats = {
    total: applications.reduce((acc, curr) => acc + parseInt(curr.acquired_credits || 0), 0),
    approved: applications.filter(a => (a.application_status || '').toLowerCase() === 'approved').length,
  };

  const toRoman = (num) => {
    const romans = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII"];
    return romans[num - 1] || num;
  };

  const categoryOptions = courseCategory.map((cat) => ({
    value: cat.id,
    label: cat.category_code
  }));

  const levelOptions = nheqfLevels.map((lvl) => ({
    value: lvl.nheqf_level,
    label: lvl.nheqf_level
  }));

  const modeOptions = [
    { value: "online", label: "Online" },
    { value: "offline", label: "Offline" },
    { value: "hybrid", label: "Hybrid" }
  ];


  const smallSelectStyles = {
    control: (base) => ({
      ...base,
      minHeight: "31px",
      height: "31px"
    }),
    valueContainer: (base) => ({
      ...base,
      height: "31px",
      padding: "0 8px"
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: "31px"
    })
  };
  useEffect(() => {
    return () => documents.forEach(doc => URL.revokeObjectURL(doc.url));
  }, [documents]);

  return (
    <StudentTemplate>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="container-fluid ">

        {/* HEADER SECTION */}
        <div className="card tranaction-card">
          <div className="card-header card-no-border">
            <div className="col-12 col-md-12">
              <h4 className="mobile text-center ">External Course Credit</h4>
              <p className="text-muted small mb-0 text-center">Register credits earned from External Platforms</p>
            </div>
          </div>
          {/* <div className="col-12 col-md-6">
            <div className="d-flex gap-3 justify-content-md-end">

              <div className="card border-0 shadow-sm px-4 py-2 d-flex flex-column align-items-center justify-content-center flex-fill flex-md-grow-0">
                <h4 className="mb-0 fw-bold text-primary">{stats.total}</h4>
                <small className="text-muted fw-bold" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>CREDITS</small>
              </div>

              <div className="card border-0 shadow-sm px-4 py-2 d-flex flex-column align-items-center justify-content-center flex-fill flex-md-grow-0">
                <h4 className="mb-0 fw-bold text-success">{stats.approved}</h4>
                <small className="text-muted fw-bold" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>APPROVED</small>
              </div>

            </div>
          </div>
        </div>

        <div className="row g-4">

          {/* SECTION 1: FORM (Full Width) */}
          <div className="col-12">
            <div className="">
              <div className="card-header bg-white border-bottom-0 pt-4 px-4">
                <h5 className="fw-bold mb-0 text-primary">
                  <i className="fas fa-plus-circle me-2"></i>New Application
                </h5>
              </div>
              <div className="card-body p-4">
                <form onSubmit={handleSubmit}>

                  {/* Course Name - Creatable Select */}
                  <div className="mb-3">
                    <label className="small text-dark fw-bold mb-2 d-block">COURSE NAME </label>
                    <CreatableSelect
                      key={formData.courseName === '' ? 'empty' : 'filled'} // ← forces remount on clear
                      isClearable
                      options={courseOptions}
                      value={formData.courseName ? { label: formData.courseName, value: formData.courseName } : null}
                      inputValue={courseInputValue}
                      onInputChange={(val) => setCourseInputValue(val)}
                      onChange={handleCourseNameChange}
                      placeholder="Select existing or type to add new..."
                      formatCreateLabel={(inputValue) => `Add New Course: "${inputValue}"`}
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                    />
                  </div>

                  <div className="row">

                    {/* Category */}
                    <div className="col-md-6">
                      <label className="small text-dark fw-bold mb-2 d-block">
                        COURSE CATEGORY</label>

                      <Select
                        options={categoryOptions}
                        value={categoryOptions.find(opt => opt.value === formData.category) ?? null}

                        onChange={(selected) =>
                          setFormData({
                            ...formData,
                            category: selected?.value || ""
                          })
                        }
                        placeholder="Select"
                      />
                    </div>

                    {/* Level */}
                    <div className="col-md-6">
                      <label className="small text-dark fw-bold mb-2 d-block">
                        NHEQF LEVEL</label>

                      <Select
                        options={levelOptions}
                        value={levelOptions.find(
                          (opt) => opt.value === formData.nheqfLevel
                        ) ?? null}
                        onChange={(selected) =>
                          setFormData({
                            ...formData,
                            nheqfLevel: selected?.value || ""
                          })
                        }
                        placeholder="Select"
                      />
                    </div>

                  </div>

                  <div className="mb-3">
                    <label className="small text-dark fw-bold mb-2 d-block">
                      DISCIPLINE
                    </label>
                    <Select
                      options={disciplineOptions}
                      value={disciplineOptions.find(
                        (opt) => opt.value === formData.discipline
                      ) ?? null}
                      onChange={(selected) =>
                        setFormData((prev) => ({
                          ...prev,
                          discipline: selected ? selected.value : '',
                        }))
                      }
                      placeholder="Select Discipline"
                      isClearable
                      styles={{
                        control: (base) => ({
                          ...base,
                          backgroundColor: '#fff',
                          border: '1px solid #dee2e6',
                          boxShadow: 'none',
                          minHeight: '40px',
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          paddingLeft: '0.75rem',
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: '#6c757d',
                          fontSize: '0.875rem',
                        }),
                        singleValue: (base) => ({
                          ...base,
                          fontSize: '0.875rem',
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                      }}
                      menuPortalTarget={document.body}
                    />
                  </div>


                  <div className="row">
                    <div className="col-md-8">
                      <label className="small text-dark fw-bold mb-2 d-block">
                        PLATFORM
                      </label>

                      <CreatableSelect
                        key={formData.platform === '' ? 'empty' : 'filled'} // ← forces remount on clear
                        isClearable
                        options={platformOptions}
                        value={formData.platform ? { label: formData.platform, value: formData.platform } : null}
                        onChange={(selected) =>
                          setFormData({ ...formData, platform: selected ? selected.value : "" })
                        }
                        onCreateOption={(inputValue) =>
                          setFormData({ ...formData, platform: inputValue })
                        }
                        placeholder="Select platform or type to add new"
                        formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="small text-dark fw-bold mb-2 d-block">
                        MODE
                      </label>

                      <Select
                        options={modeOptions}
                        value={modeOptions.find((opt) => opt.value === formData.mode) ?? null}
                        onChange={(selected) =>
                          setFormData({ ...formData, mode: selected?.value || "" })
                        }
                        placeholder="Select"
                        styles={selectStyles}

                      />
                    </div>
                  </div>

                  {/* Credits & Grade Scale Row */}
                  <div className="row g-3 mb-3">

                    {/* Course Credit */}
                    <div className="col-md-6">
                      <label className="small text-dark fw-bold mb-2 d-block">
                        COURSE CREDIT
                      </label>

                      <Select
                        options={creditSelectOptions}
                        value={creditSelectOptions.find(
                          opt => String(opt.value) === String(formData.credits)
                        ) ?? null}

                        onChange={(selected) =>
                          setFormData({ ...formData, credits: selected?.value ?? "" })
                        }
                        placeholder="Select Credits"
                      />
                    </div>

                    {/* Grade Scale */}
                    <div className="col-md-6">
                      <label className="small text-dark fw-bold mb-2 d-block">
                        GRADE SCALE
                      </label>

                      <Select
                        options={gradeScaleOptions}
                        value={gradeScaleOptions.find(
                          (opt) => opt.value === formData.gradeScale
                        ) ?? null}
                        onChange={(selected) =>
                          setFormData({
                            ...formData,
                            gradeScale: selected?.value || ""
                          })
                        }
                        placeholder="Select Scale"
                      />
                    </div>

                  </div>

                  {/* ABC Availability Toggle */}
                  <div className={`p-3 rounded-3 mb-3 border ${formData.abcAvailable ? 'bg-white border-primary' : 'bg-light border-light'}`}>
                    <div className="form-check form-switch mb-0">
                      <input className="form-check-input" type="checkbox" role="switch" id="abcSwitch"
                        name="abcAvailable" checked={formData.abcAvailable} onChange={handleChange} />
                      <label className="form-check-label fw-semibold small text-dark" htmlFor="abcSwitch">
                        Is credits available in ABC?
                      </label>
                    </div>

                    <div className={`mt-3 ${!formData.abcAvailable ? 'opacity-50 pe-none' : ''}`}>
                      <div className="row g-2">

                        {/* Grade - full width on mobile, 4 cols on desktop */}
                        <div className="col-12 col-md-4">
                          <CreatableSelect
                            options={gradeOptions}
                            value={
                              gradeOptions.find(opt => opt.value === formData.grade) ||
                              (formData.grade
                                ? { value: formData.grade, label: formData.grade }
                                : null)
                            }
                            onChange={(selected) =>
                              setFormData({
                                ...formData,
                                grade: selected?.value?.toUpperCase() || ""
                              })
                            }
                            onInputChange={(inputValue, { action }) => {
                              if (action !== "input-change") return inputValue;
                              const upper = inputValue.toUpperCase();
                              return upper.replace(/[^A-Z+-]/g, '').slice(0, 3);
                            }}
                            isValidNewOption={(inputValue) =>
                              /^[A-Z](\+{1,2}|\-{1,2})?$/.test(inputValue)
                            }
                            formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                            placeholder="Select grades or type to add new"
                            isDisabled={!formData.abcAvailable}
                            isClearable
                            styles={smallSelectStyles}
                          />
                        </div>

                        {/* Grade Point */}
                        <div className="col-6 col-md-4">
                          <input
                            type="number"
                            className={`form-control form-control-sm border text-dark ${formData.abcAvailable ? 'bg-white' : 'bg-light'}`}
                            placeholder="Grade Point"
                            name="gradePoint"
                            value={formData.gradePoint}
                            onChange={handleChange}
                            onKeyDown={(e) => {
                              if (['e', 'E', '+', '-'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            disabled={!formData.abcAvailable}
                            required={formData.abcAvailable}
                            min="0"
                            max="10"
                          />
                        </div>

                        {/* Percentage */}
                        <div className="col-6 col-md-4">
                          <input
                            type="number"
                            className={`form-control form-control-sm border text-dark ${formData.abcAvailable ? 'bg-white' : 'bg-light'}`}
                            placeholder="Percentage"
                            name="percentage"
                            value={formData.percentage}
                            onChange={(e) => {
                              let value = e.target.value;
                              if (!/^\d*\.?\d{0,2}$/.test(value)) return;
                              if (value !== "" && parseFloat(value) > 100) return;
                              handleChange(e);
                            }}
                            disabled={!formData.abcAvailable}
                            required={formData.abcAvailable}
                            min="0"
                            max="100"
                            step="0.01"
                          />
                        </div>

                      </div>
                    </div>
                  </div>

                  {/* File Upload Area */}
                  <div className="mb-4">
                    <label className="small text-dark fw-bold mb-2 d-block">CERTIFICATE UPLOAD</label>

                    {documents.length === 0 ? (
                      <div
                        className="position-relative p-4 text-center bg-white border border-primary rounded-3"
                        style={{ borderStyle: 'dashed', borderColor: '#ccc' }}
                      >
                        <input
                          type="file"
                          className="position-absolute top-0 start-0 w-100 h-100 opacity-0 cursor-pointer"
                          accept=".pdf,.jpg,.png"
                          multiple
                          onChange={handleFileChange}
                        />
                        <div className="mb-2">
                          <i className="fas fa-cloud-upload-alt fa-2x text-primary"></i>
                        </div>
                        <p className="small mb-0 text-dark">
                          Tap to upload documents
                        </p>
                      </div>
                    ) : (
                      <div className="d-flex flex-column gap-2">
                        {documents.map((doc, index) => (
                          <div
                            key={index}
                            className="d-flex align-items-center justify-content-between p-3 bg-white rounded-3 border"
                          >
                            <div className="d-flex align-items-center gap-3 overflow-hidden">
                              <div className="bg-light p-2 rounded text-dark">
                                <i className="fas fa-file-alt fa-lg"></i>
                              </div>

                              <div className="text-truncate">
                                <p
                                  className="mb-0 fw-semibold text-dark text-truncate"
                                  style={{ maxWidth: '200px' }}
                                >
                                  {doc.name}
                                </p>
                                <p className="mb-0 text-muted small">
                                  {doc.size}
                                </p>
                              </div>
                            </div>

                            <div className="d-flex gap-2">
                              <button
                                type="button"
                                onClick={() => handlePreview(doc)}
                                className="btn btn-sm btn-outline-secondary"
                                title="Preview"
                              >
                                <i className="fas fa-eye"></i>
                              </button>

                              <button
                                type="button"
                                onClick={() => removeFile(index)}
                                className="btn btn-sm btn-outline-danger"
                                title="Remove"
                              >
                                <i className="fas fa-trash-alt"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Submit Button - Disabled if ABC is false */}
                  <button
                    type="submit"
                    disabled={!formData.abcAvailable || isLoading}
                    className="btn btn-primary w-100 py-3 rounded-3 fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2"
                  >
                    {isLoading ? (
                      <div className="spinner-border spinner-border-sm" role="status"></div>
                    ) : (
                      <>Submit Application <i className="fas fa-paper-plane"></i></>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* SECTION 2: LIST (Full Width) */}

        </div>
      </div>
      <div className='container-fluid'>
        <div className="col-12">
          <div className="card shadow-sm rounded-4 h-100 overflow-hidden">
            <div className="card-header bg-white border-bottom pt-4 px-4 pb-3 d-flex justify-content-between align-items-center">
              <h5 className="fw-bold mb-0 text-dark">Submission History</h5>
              <span className="badge bg-light text-dark border">{applications.length} Records</span>
            </div>

            {/* DESKTOP VIEW: TABLE */}
            <div className="d-none d-md-block card-body p-0">
              {applications.length === 0 ? (
                <div className="text-center py-5">
                  <p className="text-muted">No credit applications found.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="ps-4 py-3 small text-muted text-uppercase text-center">Course</th>
                        <th className="pe-4 py-3 small text-muted text-uppercase text-center">
                          Discipline Details
                        </th>
                        <th className="py-3 small text-muted text-uppercase text-center">Credits/Mode</th>
                        <th className="py-3 small text-muted text-uppercase text-center">Grades</th>

                        <th className="pe-4 py-3 small text-muted text-uppercase text-center">
                          Remarks
                        </th>
                        <th className="py-3 small text-muted text-uppercase text-center">Status</th>

                      </tr>
                    </thead>
                    <tbody>
                      {applications.map(app => (
                        <tr key={app.application_id}>
                          <td className="ps-4 text-center">
                            <div className="fw-bold text-dark">{app.course_name}</div>
                            <small className="text-muted">{app.platform} • NHEQF Level : {app.course_level}</small>
                          </td>
                          <td className="text-center">
                            <div className="d-flex flex-column align-items-center gap-1">

                              <span className="fw-semibold text-dark">
                                {app.discipline_name}
                              </span>

                              <span className="badge bg-light text-muted border px-2 py-1">
                                Course Category: {app.category_code}
                              </span>

                            </div>
                          </td>
                          <td className='text-center'>
                            <div className="d-flex flex-column">
                              <span className="fw-semibold text-dark">{app.acquired_credits} Credits</span>
                              <span className="small text-muted text-capitalize">{app.mode}</span>
                            </div>
                          </td>
                          <td className="text-center">
                            <div className="d-flex flex-column align-items-center">

                              {/* Line 1 */}
                              <div className="d-flex align-items-center gap-2 flex-wrap justify-content-center">

                                <span className="badge bg-light text-dark border">
                                  {app.grade} ({app.percentage}%)
                                </span>

                                <span className="badge bg-light text-dark border">
                                  GP: {app.grade_point}
                                </span>

                              </div>

                              {/* Line 2 */}
                              <span className="small text-muted mt-1">
                                Grade Scale: {app.grade_scale}
                              </span>

                            </div>
                          </td>

                          <td className="text-center">
                            {app.remarks ? (
                              <span
                                className="text-dark small d-inline-block"
                                style={{ maxWidth: '150px' }}
                                title={app.remarks}
                              >
                                {app.remarks}
                              </span>
                            ) : (
                              <span className="text-muted small">-</span>
                            )}
                          </td>
                          <td className='text-center'>
                            <span className={`badge rounded-pill ${getStatusBadge(app.application_label)} border d-inline-flex align-items-center gap-1 px-2 py-1`}>
                              {app.application_label}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* MOBILE VIEW: CARDS */}
            <div className="d-md-none card-body p-3 bg-light">
              {applications.length === 0 ? (
                <div className="text-center py-5 text-muted">No applications found.</div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {applications.map(app => (
                    <div key={app.application_id} className="card border-0 shadow-sm p-3 rounded-3">

                      {/* Header */}
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <h6 className="fw-bold text-dark mb-0">{app.course_name}</h6>
                          <small className="text-muted">{app.platform}</small>
                        </div>

                        <span className={`badge rounded-pill ${getStatusBadge(app.application_label)}`}>
                          {app.application_label}
                        </span>
                      </div>
                      <div className="border-top my-2"></div>

                      {/* Academic Details */}
                      <div className="small mb-2">

                        <div className="d-flex flex-wrap align-items-center gap-2">
                          <span className="text-muted">Discipline:</span>
                          <span className="fw-semibold">{app.discipline_name}</span>
                        </div>

                        <div className="mt-1 d-flex flex-wrap align-items-center gap-2">
                          <span className="text-muted">Course Category: </span>
                          <span className="text-capitalize">
                            {app.category_code}
                          </span>
                        </div>

                      </div>

                      {/* Credits + Mode */}
                      <div className="d-flex flex-wrap gap-3 small mb-2">
                        <div>
                          <span className="text-muted">Credits: </span>
                          <span className="fw-semibold">{app.acquired_credits}</span>
                        </div>

                        <div>
                          <span className="text-muted">Mode: </span>
                          <span className="text-capitalize">{app.mode}</span>
                        </div>
                        <div>
                          <span className="text-muted">NHEQF Level: </span>
                          <span className="text-capitalize">{app.course_level}</span>
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="border-top my-2"></div>

                      {/* Grades */}
                      {app.ABC_status == 1 && (
                        <div className="small mb-2">

                          {/* Line 1 */}
                          <div className="d-flex flex-wrap align-items-center gap-2">
                            <span className="text-muted">Grade:</span>

                            <span className="fw-semibold text-dark">
                              {app.grade} ({app.percentage}%)
                            </span>

                            <span className="text-muted">|</span>

                            <span className="text-dark">
                              GP: {app.grade_point}
                            </span>
                          </div>

                          {/* Line 2 */}
                          <div className="mt-1">
                            <span className="text-muted">Grade Scale: </span>
                            <span className="text-dark">{app.grade_scale}</span>
                          </div>

                        </div>
                      )}

                      {/* Divider */}


                      {/* Remarks (only if exists) */}
                      {app.remarks && (
                        <>
                          <div className="border-top my-2"></div>

                          <div className="small">
                            <span className="text-muted">Remark: </span>
                            <span className="text-dark">{app.remarks}</span>
                          </div>
                        </>
                      )}

                      {/* Footer */}
                      <div className="d-flex justify-content-between align-items-center border-top pt-2 mt-2">
                        <small className="text-muted">
                          <i className="fas fa-calendar-alt me-1"></i>
                          {formatDate(app.created_at)}
                        </small>
                      </div>

                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* DOCUMENT PREVIEW MODAL */}
      {previewDoc && (
        <div
          className="modal fade show d-block d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content shadow-lg border-0">

              <div className="modal-header border-bottom">
                <h6 className="modal-title fw-bold text-truncate">
                  <i className="fas fa-file-alt me-2 text-primary"></i>
                  {previewDoc.name}
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setPreviewDoc(null)}
                ></button>
              </div>

              <div className="modal-body bg-light p-0 text-center">
                {previewDoc.file.type.startsWith("image/") ? (
                  <img
                    src={previewDoc.url}
                    alt="preview"
                    className="img-fluid"
                    style={{ maxHeight: '70vh' }}
                  />
                ) : (
                  <iframe
                    src={`${previewDoc.url}#toolbar=0`}
                    className="w-100"
                    style={{ height: '60vh', border: 'none' }}
                    title="pdf"
                  />
                )}
              </div>

            </div>
          </div>
        </div>
      )}

    </StudentTemplate>

  );

};

export default AcademicCredit;